/**
 * Fork-specific seeding: fund accounts with tokens via storage slot manipulation.
 *
 * On forked chains, we can't call `mint()` on real tokens (no owner access).
 * Instead, we manipulate storage slots directly via `setStorageAt`.
 */

import { POOL_MANAGER_ABI, sortTokens } from "@gotts.ai/chain";
import { POOL_MODIFY_LIQUIDITY_TEST_ABI } from "@gotts.ai/devenv";
import { encodeFunctionData, encodePacked, keccak256, pad, toHex } from "viem";

import { PREFUNDED_ACCOUNTS } from "../runtime/accounts.js";

import type {
  Address,
  Hex,
  PublicClient,
  TestClient,
  WalletClient,
} from "viem";

/** Well-known balanceOf storage slot indices for common tokens. */
export const TOKEN_BALANCE_SLOTS: Record<
  string,
  { address: Address; slot: number }
> = {
  // Ethereum mainnet
  USDC_ETH: { address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", slot: 9 },
  USDT_ETH: { address: "0xdAC17F958D2ee523a2206206994597C13D831ec7", slot: 2 },
  DAI_ETH: { address: "0x6B175474E89094C44Da98b954EedeAC495271d0F", slot: 2 },
  WBTC_ETH: { address: "0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599", slot: 0 },
  WETH_ETH: { address: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", slot: 3 },

  // Base
  USDC_BASE: { address: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", slot: 9 },
  WETH_BASE: { address: "0x4200000000000000000000000000000000000006", slot: 3 },
};

/**
 * Compute the storage key for a mapping(address => uint256) at a given slot.
 * Solidity stores mapping values at keccak256(abi.encode(key, slot)).
 */
function balanceStorageKey(account: Address, slot: number): `0x${string}` {
  return keccak256(
    encodePacked(
      ["bytes32", "bytes32"],
      [pad(account, { size: 32 }), pad(toHex(BigInt(slot)), { size: 32 })],
    ),
  );
}

/**
 * Set an ERC-20 token balance for an account via storage slot manipulation.
 */
export async function fundAccountWithToken(
  testClient: TestClient,
  tokenAddress: Address,
  slot: number,
  account: Address,
  amount: bigint,
): Promise<void> {
  const storageKey = balanceStorageKey(account, slot);
  // Retry to tolerate fork RPC rate-limiting (setStorageAt → tevm_setAccount
  // reads the account from the remote before writing).
  let lastErr: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      await testClient.setStorageAt({
        address: tokenAddress,
        index: storageKey,
        value: pad(toHex(amount), { size: 32 }),
      });
      return;
    } catch (err) {
      lastErr = err;
      await new Promise((r) => setTimeout(r, 200 * (attempt + 1)));
    }
  }
  throw lastErr;
}

/** Token funding distribution tiers (matching from-scratch seeding). */
const TIER_AMOUNTS: bigint[] = [
  10_000_000n, // deployer: 10M
  1_000_000n, // alice: 1M
  500_000n, // bob: 500K
  100_000n, // carol: 100K
  50_000n, // dave: 50K
  10_000n, // eve: 10K
];

/**
 * Fund all 20 prefunded accounts with tiered token balances.
 * Uses storage slot manipulation for fork mode.
 */
export async function seedForkAccounts(
  testClient: TestClient,
  chainId: number,
  onAction?: (action: string) => void | Promise<void>,
): Promise<void> {
  const prefix = chainId === 8453 ? "_BASE" : "_ETH";
  const tokens = Object.entries(TOKEN_BALANCE_SLOTS)
    .filter(([key]) => key.endsWith(prefix))
    .map(([key, val]) => ({
      name: key.replace(prefix, ""),
      ...val,
    }));

  const totalOps = tokens.length * PREFUNDED_ACCOUNTS.length;
  let opCount = 0;

  for (const token of tokens) {
    for (let i = 0; i < PREFUNDED_ACCOUNTS.length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const account = PREFUNDED_ACCOUNTS[i]!;
      const tierIndex = Math.min(i, TIER_AMOUNTS.length - 1);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const amount = TIER_AMOUNTS[tierIndex]!;

      opCount++;
      await onAction?.(
        `Funding ${token.name} — account ${String(opCount)}/${String(totalOps)}`,
      );

      // Scale by token decimals (USDC/USDT = 6, others = 18, WBTC = 8)
      let decimals = 18n;
      if (token.name === "USDC" || token.name === "USDT") decimals = 6n;
      if (token.name === "WBTC") decimals = 8n;

      await fundAccountWithToken(
        testClient,
        token.address,
        token.slot,
        account.address,
        amount * 10n ** decimals,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// V4 Pool Seeding (fork mode)
// ---------------------------------------------------------------------------

/** Minimal ERC-20 approve ABI fragment. */
const ERC20_APPROVE_ABI = [
  {
    type: "function",
    name: "approve",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ type: "bool" }],
    stateMutability: "nonpayable",
  },
] as const;

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000" as Address;
const ZERO_BYTES32 =
  "0x0000000000000000000000000000000000000000000000000000000000000000" as Hex;
const MAX_UINT256 = 2n ** 256n - 1n;

/** sqrtPriceX96 for stablecoin(token0)/WETH(token1): price = 1/2000 */
const SQRT_PRICE_STABLE_WETH = 1771595571142957166518320255467n;
/** sqrtPriceX96 for WETH(token0)/stablecoin(token1): price = 2000 */
const SQRT_PRICE_WETH_STABLE = 3543191142285914493284720n;

const TICK_LOWER = -887220;
const TICK_UPPER = 887220;
const V4_LIQUIDITY_DELTA = 10n ** 14n;

/** V4 pool definitions for fork mode. */
const FORK_V4_POOLS = [
  { label: "WETH/USDC", tokenKeys: ["weth", "usdc"] as const },
  { label: "WETH/DAI", tokenKeys: ["weth", "dai"] as const },
];

/**
 * Initialize V4 pools and add liquidity on a forked chain.
 *
 * Uses the same pool parameters as devenv from-scratch mode:
 * fee=3000, tickSpacing=60, hooks=0x0, full-range liquidity.
 */
export async function seedForkV4Pools(
  walletClient: WalletClient,
  publicClient: PublicClient,
  deployer: Address,
  poolManager: Address,
  modifyLiquidityTest: Address,
  tokens: Record<string, Address>,
  onAction?: (action: string) => void | Promise<void>,
): Promise<void> {
  for (let i = 0; i < FORK_V4_POOLS.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const pool = FORK_V4_POOLS[i]!;
    const tokenA = tokens[pool.tokenKeys[0]];
    const tokenB = tokens[pool.tokenKeys[1]];
    // Skip pools where tokens aren't available on this chain (e.g. no DAI on Base)
    if (!tokenA || !tokenB || tokenA.length < 42 || tokenB.length < 42)
      continue;

    const [currency0, currency1] = sortTokens(tokenA, tokenB);

    // Determine sqrtPriceX96 based on token order
    const weth = tokens.weth?.toLowerCase();
    const sqrtPriceX96 =
      currency0.toLowerCase() === weth
        ? SQRT_PRICE_WETH_STABLE
        : SQRT_PRICE_STABLE_WETH;

    const poolKey = {
      currency0,
      currency1,
      fee: 3000,
      tickSpacing: 60,
      hooks: ZERO_ADDRESS,
    };

    // Step 1: Initialize pool (may already exist on fork — ignore errors)
    await onAction?.(
      `V4 pool ${String(i + 1)}/${String(FORK_V4_POOLS.length)}: initializing ${pool.label}`,
    );
    try {
      const initData = encodeFunctionData({
        abi: POOL_MANAGER_ABI,
        functionName: "initialize",
        args: [poolKey, sqrtPriceX96],
      });
      const initHash = await walletClient.sendTransaction({
        account: deployer,
        to: poolManager,
        data: initData,
        chain: walletClient.chain,
      });
      await publicClient.waitForTransactionReceipt({ hash: initHash });
    } catch {
      // Pool may already be initialized on the fork — continue
    }

    // Step 2: Approve tokens to PoolModifyLiquidityTest
    for (const token of [currency0, currency1]) {
      const approveData = encodeFunctionData({
        abi: ERC20_APPROVE_ABI,
        functionName: "approve",
        args: [modifyLiquidityTest, MAX_UINT256],
      });
      const approveHash = await walletClient.sendTransaction({
        account: deployer,
        to: token,
        data: approveData,
        chain: walletClient.chain,
      });
      await publicClient.waitForTransactionReceipt({ hash: approveHash });
    }

    // Step 3: Add full-range liquidity
    await onAction?.(
      `V4 pool ${String(i + 1)}/${String(FORK_V4_POOLS.length)}: adding ${pool.label} liquidity`,
    );
    const modifyData = encodeFunctionData({
      abi: POOL_MODIFY_LIQUIDITY_TEST_ABI,
      functionName: "modifyLiquidity",
      args: [
        poolKey,
        {
          tickLower: TICK_LOWER,
          tickUpper: TICK_UPPER,
          liquidityDelta: V4_LIQUIDITY_DELTA,
          salt: ZERO_BYTES32,
        },
        "0x" as Hex,
      ],
    });
    const modifyHash = await walletClient.sendTransaction({
      account: deployer,
      to: modifyLiquidityTest,
      data: modifyData,
      chain: walletClient.chain,
    });
    await publicClient.waitForTransactionReceipt({ hash: modifyHash });
  }
}
